import React from 'react';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { GetDownloadsDocumentsDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import { FiltrYearTypeObj } from '../../../common/pokCore/validation/enums';
import { teamName } from '../../../utils/teamName';

import { DownloadZIPLink } from './DownloadZIPLink';

interface DownloadDocumentsSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  // onRowClick: (key: string) => Promise<void> | void | undefined;
  // refresh?: boolean;
}

export const downloadDocumentsForGrid = (
  t: TFunction<'commonLng', 'commonLng'>,
  tk: TKeys<'commonLng'>,
  downloadDoc: GetDownloadsDocumentsDto,
) => {
  return {
    key: downloadDoc.id,
    values: {
      'downloadsDocuments.status': !downloadDoc.status
        ? t(tk.download.statusWaiting)
        : downloadDoc.fileName
          ? t(tk.download.statusReady)
          : downloadDoc.info
            ? t(tk.download.statusFinished)
            : t(tk.download.statusCalculating),
      'downloadsDocuments.info':
        downloadDoc.info === 'Nie znaleziono danych do pobrania.'
          ? t(tk.download.notFoundFiles)
          : downloadDoc.info,
      'creator.name': downloadDoc.creator.name,
      'project.number': downloadDoc.project?.number,
      'budget.name': downloadDoc.budget?.name,
      'team.name': teamName(downloadDoc.team),
      'client.name': downloadDoc.client?.name,
      'company.shortname': downloadDoc.company?.shortname,
      'downloadsDocuments.creationTs': momencik(
        downloadDoc.creationTs,
        'YYYY-MM-DD HH:mm:ss',
      ),
      'downloadsDocuments.salesInvoices': downloadDoc.salesInvoices
        ? t(tk.common.yes)
        : t(tk.common.no),
      'downloadsDocuments.salesInvoicesCopies': downloadDoc.salesInvoicesCopies
        ? t(tk.common.yes)
        : t(tk.common.no),
      'downloadsDocuments.purchaseInvoices': downloadDoc.purchaseInvoices
        ? t(tk.common.yes)
        : t(tk.common.no),
      'downloadsDocuments.contracts': downloadDoc.contracts
        ? t(tk.common.yes)
        : t(tk.common.no),
      'downloadsDocuments.projectAttachments': downloadDoc.projectAttachments
        ? t(tk.common.yes)
        : t(tk.common.no),
      'downloadsDocuments.yearType': t(FiltrYearTypeObj[downloadDoc.yearType]),
      // 'downloadsDocuments.attachmentsTypes': '',
      'downloadsDocuments.rangeMonths':
        momencik(downloadDoc.rangeMonthFrom, 'YYYY-MM') +
        ' - ' +
        momencik(downloadDoc.rangeMonthTo, 'YYYY-MM'),
      zip: downloadDoc.fileName ? (
        <DownloadZIPLink fileName={downloadDoc.fileName} />
      ) : (
        <> </>
      ),
    },
  };
};

export const DownloadDocumentsSearch: React.FC<
  DownloadDocumentsSearchProps
> = props => {
  const { t, tk } = useTranslation('commonLng');

  const columns: GridColumn[] = [
    {
      header: '',
      property: 'zip',
      noSorting: true,
      noFilter: true,
    },
    {
      header: t(tk.common.status),
      property: 'downloadsDocuments.status',
      noSorting: true,
      noFilter: true,
    },
    { header: t(tk.download.info), property: 'downloadsDocuments.info' },
    { header: t(tk.common.creator), property: 'creator.name' },
    {
      header: t(tk.download.creationTs),
      property: 'downloadsDocuments.creationTs',
    },
    { header: t(tk.download.companyShortname), property: 'company.shortname' },
    { header: t(tk.download.budgetName), property: 'budget.name' },
    { header: t(tk.download.teamName), property: 'team.name' },
    { header: t(tk.download.clientName), property: 'client.name' },
    { header: t(tk.prLang.project), property: 'project.number' },
    {
      header: t(tk.download.salesInvoices),
      property: 'downloadsDocuments.salesInvoices',
      noFilter: true,
    },
    {
      header: t(tk.download.salesInvoicesCopies),
      property: 'downloadsDocuments.salesInvoicesCopies',
      noFilter: true,
    },
    {
      header: t(tk.download.purchaseInvoices),
      property: 'downloadsDocuments.purchaseInvoices',
      noFilter: true,
    },
    {
      header: t(tk.download.contracts),
      property: 'downloadsDocuments.contracts',
      noFilter: true,
    },
    {
      header: t(tk.download.attachments),
      property: 'downloadsDocuments.projectAttachments',
      noFilter: true,
    },
    {
      header: t(tk.download.filterName),
      property: 'downloadsDocuments.yearType',
      noFilter: true,
      noSorting: true,
    },
    // {
    //   header: t(tk.download.attachmentsTypes),
    //   property: 'downloadsDocuments.attachmentsTypes',
    //   noFilter: true,
    //   noSorting: true,
    // },
    {
      header: t(tk.download.rangeMonths),
      property: 'downloadsDocuments.rangeMonths',
      noFilter: true,
      noSorting: true,
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="downloadsDocuments.creationTs"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.download.noData)}
      // onRowClick={props.onRowClick}
      withFilterInColumns={true}
      // refresh={props.refresh}
      withHistory={true}
    />
  );
};
